import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MdLocalShipping,
  MdCheckCircle,
  MdLock,
  MdClose,
  MdThumbUp,
  MdThumbDown,
} from 'react-icons/md';
import { authenticatedFetch } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useToast } from '../shared/Toast';

interface LogisticPartner {
  id: string;
  name: string;
}

interface AdminBuyerShippingSectionProps {
  po: any;
  poId: string;
  canConfigure: boolean;
}

/** Matches API PoDeliveryService.buildShippingRequestFingerprint */
function buildShippingRequestFingerprint(
  po: any,
  addressOverride?: { street?: string; city?: string; country?: string }
): string {
  const dd = po?.deliveryDetails || {};
  const addr = addressOverride || po?.shippingAddress || {};
  const portId = String(dd.portId?._id || dd.portId || '').trim();
  const warehouseId = String(dd.warehouseId?._id || dd.warehouseId || '').trim();
  return [
    String(dd.deliveryRequiredAt || '').trim(),
    String(dd.deliveryDestination || '').trim(),
    portId,
    String(dd.portName || '').trim(),
    String(dd.portAddress || '').trim(),
    warehouseId,
    String(dd.warehouseSnapshot?.address || '').trim(),
    String(dd.vesselNextPort || '').trim(),
    String(addr.street || '').trim(),
    String(addr.city || '').trim(),
    String(addr.country || '').trim(),
  ]
    .map((s) => s.toLowerCase())
    .join('|');
}

function invalidateBuyerPo(queryClient: ReturnType<typeof useQueryClient>, poId: string) {
  queryClient.invalidateQueries({ queryKey: ['admin-po-details'] });
  queryClient.invalidateQueries({ queryKey: ['admin-your-rfq-po-enriched'] });
  queryClient.invalidateQueries({ queryKey: ['admin-your-rfq-purchase-order'] });
  queryClient.invalidateQueries({ queryKey: ['admin-procurement-invoices', poId] });
  queryClient.invalidateQueries({ queryKey: ['admin-buyer-payment-schedule', poId] });
}

export function AdminBuyerShippingSection({
  po,
  poId,
  canConfigure,
}: AdminBuyerShippingSectionProps) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [showSelfModal, setShowSelfModal] = useState(false);
  const [logisticPartnerId, setLogisticPartnerId] = useState('');
  const [customPartnerName, setCustomPartnerName] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [awbNumber, setAwbNumber] = useState('');
  const [destStreet, setDestStreet] = useState(po?.shippingAddress?.street || '');
  const [destCity, setDestCity] = useState(po?.shippingAddress?.city || '');
  const [destCountry, setDestCountry] = useState(po?.shippingAddress?.country || '');

  const { data: partners = [] } = useQuery<LogisticPartner[]>({
    queryKey: ['admin-logistic-partners'],
    queryFn: async () => {
      const res = await authenticatedFetch('/api/v1/admin/logistic-partners');
      if (!res.ok) return [];
      const json = await res.json();
      return json.data || [];
    },
    staleTime: 30 * 60 * 1000,
  });

  const shippingMode = po.shippingMode as 'vendor' | 'self' | null | undefined;
  const shippingLocked = !!po.shippingModeConfirmed;
  const proformaApproved = po?.metadata?.proformaInvoiceApproved === true;
  const details = po.shippingDetails || {};
  const chargesStatus = details.chargesStatus as string | undefined;
  const hasVendorCharges =
    (details.vendorCharges?.length > 0 || details.taxes?.length > 0);
  const canChangeMode = canConfigure && !shippingLocked && proformaApproved;

  const shippingChargesAlreadyRequested = useMemo(() => {
    if (!po?.metadata?.vendorShippingRequestedAt) return false;
    const requestedFp = po.metadata?.vendorShippingRequestFingerprint as string | undefined;
    if (!requestedFp) return true;
    return (
      buildShippingRequestFingerprint(po, {
        street: destStreet.trim(),
        city: destCity.trim(),
        country: destCountry.trim(),
      }) === requestedFp
    );
  }, [po, destStreet, destCity, destCountry]);

  const selectedPartner = partners.find((p) => p.id === logisticPartnerId);
  const partnerName =
    logisticPartnerId === 'other'
      ? customPartnerName.trim()
      : selectedPartner?.name || '';

  const updateModeMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const res = await authenticatedFetch(`/api/v1/admin/pos/${poId}/shipping-mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const err = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(err.error || 'Failed to update shipping mode');
      return err;
    },
    onSuccess: (_, payload) => {
      invalidateBuyerPo(queryClient, poId);
      if (payload.shippingMode === 'self') {
        setShowSelfModal(false);
        showToast('Self shipment details saved. Confirm to lock the selection.', 'success');
      } else if (payload.confirm) {
        showToast('Shipping selection confirmed', 'success');
      } else {
        showToast('Vendor shipping selected. Enter destination and request charges.', 'success');
      }
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const requestChargesMutation = useMutation({
    mutationFn: async () => {
      const res = await authenticatedFetch(
        `/api/v1/admin/pos/${poId}/request-vendor-shipping`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            shippingAddress: {
              street: destStreet.trim(),
              city: destCity.trim(),
              country: destCountry.trim(),
            },
          }),
        }
      );
      const err = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(err.error || 'Failed to request shipping charges');
      return err;
    },
    onSuccess: () => {
      invalidateBuyerPo(queryClient, poId);
      showToast('Vendor notified to submit shipping charges', 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const approveChargesMutation = useMutation({
    mutationFn: async () => {
      const res = await authenticatedFetch(
        `/api/v1/admin/pos/${poId}/shipping-charges/approve`,
        { method: 'POST' }
      );
      const err = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(err.error || 'Failed to accept shipping charges');
      return err;
    },
    onSuccess: () => {
      invalidateBuyerPo(queryClient, poId);
      showToast('Shipping charges accepted. Final invoice issued for review.', 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const rejectChargesMutation = useMutation({
    mutationFn: async () => {
      const res = await authenticatedFetch(
        `/api/v1/admin/pos/${poId}/shipping-charges/reject`,
        { method: 'POST' }
      );
      const err = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(err.error || 'Failed to reject shipping charges');
      return err;
    },
    onSuccess: () => {
      invalidateBuyerPo(queryClient, poId);
      showToast('Charges rejected. Enter logistic partner and AWB for self shipment.', 'info');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const openSelfModal = () => {
    if (shippingLocked) return;
    setLogisticPartnerId(details.logisticPartnerId || '');
    setCustomPartnerName(
      details.logisticPartnerName && details.logisticPartnerId === 'other'
        ? details.logisticPartnerName
        : ''
    );
    setTrackingNumber(details.trackingNumber || '');
    setAwbNumber(details.awbNumber || '');
    setShowSelfModal(true);
  };

  const handleProceedSelf = () => {
    if (!partnerName) {
      showToast('Select a logistic partner or enter a courier name', 'error');
      return;
    }
    if (!trackingNumber.trim() && !awbNumber.trim()) {
      showToast('Enter AWB or tracking number', 'error');
      return;
    }
    updateModeMutation.mutate({
      shippingMode: 'self',
      logisticPartnerId: logisticPartnerId || 'other',
      logisticPartnerName: partnerName,
      trackingNumber: trackingNumber.trim(),
      awbNumber: awbNumber.trim() || trackingNumber.trim(),
    });
  };

  const handleConfirm = () => {
    if (!shippingMode) {
      showToast('Select vendor or self shipment first', 'error');
      return;
    }
    if (shippingMode === 'self' && !details.logisticPartnerName && !details.awbNumber) {
      showToast('Complete self shipment details before confirming', 'error');
      return;
    }
    if (
      !window.confirm(
        `Confirm ${shippingMode === 'self' ? 'self shipment' : 'vendor shipment'}? You will not be able to change this afterwards.`
      )
    ) {
      return;
    }
    const payload: Record<string, unknown> = { shippingMode, confirm: true };
    if (shippingMode === 'self') {
      payload.logisticPartnerId = details.logisticPartnerId || logisticPartnerId || 'other';
      payload.logisticPartnerName = details.logisticPartnerName || partnerName;
      payload.trackingNumber = details.trackingNumber || trackingNumber;
      payload.awbNumber = details.awbNumber || details.trackingNumber || awbNumber;
    }
    updateModeMutation.mutate(payload);
  };

  if (!canConfigure && !shippingMode && !proformaApproved) return null;

  if (!proformaApproved) {
    return (
      <div className="rounded-2xl border border-[#0088cc]/20 bg-[hsl(var(--card))] shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-[#0088cc]/15">
          <h2 className="text-base font-bold flex items-center gap-2">
            <MdLocalShipping className="w-5 h-5 text-[#0088cc]" />
            Shipping Configuration
          </h2>
        </div>
        <div className="p-6">
          <p className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2">
            <MdLock className="w-4 h-4 shrink-0" />
            Accept the proforma invoice first, then choose vendor shipping or self shipment (AWB).
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-2xl border border-[#0088cc]/20 bg-[hsl(var(--card))] shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-[#0088cc]/15 flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-base font-bold flex items-center gap-2">
            <MdLocalShipping className="w-5 h-5 text-[#0088cc]" />
            Shipping Configuration
          </h2>
          <div className="flex items-center gap-2">
            {shippingLocked && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
                <MdLock className="w-3 h-3" /> Confirmed
              </span>
            )}
            {shippingMode && (
              <span
                className={cn(
                  'text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider',
                  shippingMode === 'self' ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'
                )}
              >
                {shippingMode === 'self' ? 'Self Shipment' : 'Vendor Shipment'}
              </span>
            )}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {(canChangeMode || shippingMode) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => {
                  if (shippingLocked || shippingMode === 'vendor') return;
                  updateModeMutation.mutate({ shippingMode: 'vendor' });
                }}
                disabled={!canChangeMode || updateModeMutation.isPending}
                className={cn(
                  'p-5 rounded-xl border-2 text-left transition-all',
                  shippingMode === 'vendor'
                    ? 'border-emerald-500 bg-emerald-500/10'
                    : 'border-[#0088cc]/20 hover:border-emerald-400',
                  !canChangeMode && 'opacity-70 cursor-not-allowed'
                )}
              >
                <p className="font-bold flex items-center gap-2">
                  Ask for Shipping Charges
                  {shippingMode === 'vendor' && <MdCheckCircle className="w-4 h-4 text-emerald-600" />}
                </p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  Vendor ships to your destination and submits shipping charges for approval.
                </p>
              </button>
              <button
                type="button"
                onClick={openSelfModal}
                disabled={!canChangeMode || updateModeMutation.isPending}
                className={cn(
                  'p-5 rounded-xl border-2 text-left transition-all',
                  shippingMode === 'self'
                    ? 'border-indigo-500 bg-indigo-500/10'
                    : 'border-[#0088cc]/20 hover:border-indigo-400',
                  !canChangeMode && 'opacity-70 cursor-not-allowed'
                )}
              >
                <p className="font-bold flex items-center gap-2">
                  Self Shipment
                  {shippingMode === 'self' && <MdCheckCircle className="w-4 h-4 text-indigo-600" />}
                </p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  Arrange your own courier and enter AWB / tracking number.
                </p>
              </button>
            </div>
          )}

          {shippingMode === 'self' && (
            <div className="p-5 rounded-xl border border-indigo-500/30 bg-indigo-500/10 space-y-3">
              <p className="font-bold text-sm text-indigo-700 dark:text-indigo-300">Self Shipment Details</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[10px] font-bold uppercase text-[#0088cc]/65">Logistic Partner</p>
                  <p className="font-semibold">{details.logisticPartnerName || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase text-[#0088cc]/65">AWB / Tracking</p>
                  <p className="font-semibold">{details.awbNumber || details.trackingNumber || '—'}</p>
                </div>
              </div>
              {canChangeMode && (
                <button
                  type="button"
                  onClick={openSelfModal}
                  className="text-xs font-bold text-indigo-700 hover:text-indigo-900"
                >
                  Edit AWB / courier
                </button>
              )}
            </div>
          )}

          {shippingMode === 'vendor' && (
            <div className="p-5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 space-y-4">
              <p className="font-bold text-sm text-emerald-700 dark:text-emerald-300">Delivery destination</p>
              {canChangeMode && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    className="rounded-lg border border-[#0088cc]/20 bg-[hsl(var(--background))] px-3 py-2 text-sm"
                    placeholder="Street / port / jetty"
                    value={destStreet}
                    onChange={(e) => setDestStreet(e.target.value)}
                  />
                  <input
                    className="rounded-lg border border-[#0088cc]/20 bg-[hsl(var(--background))] px-3 py-2 text-sm"
                    placeholder="City"
                    value={destCity}
                    onChange={(e) => setDestCity(e.target.value)}
                  />
                  <input
                    className="rounded-lg border border-[#0088cc]/20 bg-[hsl(var(--background))] px-3 py-2 text-sm"
                    placeholder="Country"
                    value={destCountry}
                    onChange={(e) => setDestCountry(e.target.value)}
                  />
                </div>
              )}
              {!hasVendorCharges && canChangeMode && (
                <div className="space-y-2">
                  <button
                    type="button"
                    disabled={
                      requestChargesMutation.isPending || shippingChargesAlreadyRequested
                    }
                    onClick={() => requestChargesMutation.mutate()}
                    className="rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold uppercase tracking-widest text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {requestChargesMutation.isPending
                      ? 'Requesting…'
                      : shippingChargesAlreadyRequested
                        ? 'Shipping charges requested'
                        : 'Request shipping charges'}
                  </button>
                  {shippingChargesAlreadyRequested && (
                    <p className="text-xs text-emerald-800/80 dark:text-emerald-300/80">
                      Update shipping port or warehouse address to request charges again.
                    </p>
                  )}
                </div>
              )}
              {hasVendorCharges && (
                <div className="space-y-2 text-sm">
                  {(details.vendorCharges || []).map((charge: any, idx: number) => (
                    <div key={`c-${idx}`} className="flex justify-between gap-3">
                      <span>{charge.label}</span>
                      <span className="font-bold">
                        {po.currency} {Number(charge.amount || 0).toFixed(2)}
                      </span>
                    </div>
                  ))}
                  {(details.taxes || []).map((tax: any, idx: number) => (
                    <div key={`t-${idx}`} className="flex justify-between gap-3">
                      <span>
                        {tax.label}
                        {tax.rate ? ` (${tax.rate}%)` : ''}
                      </span>
                      <span className="font-bold">
                        {po.currency} {Number(tax.amount || 0).toFixed(2)}
                      </span>
                    </div>
                  ))}
                  {chargesStatus === 'submitted' && canChangeMode && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => approveChargesMutation.mutate()}
                        disabled={approveChargesMutation.isPending}
                        className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold uppercase tracking-widest text-white"
                      >
                        <MdThumbUp className="h-4 w-4" /> Accept charges
                      </button>
                      <button
                        type="button"
                        onClick={() => rejectChargesMutation.mutate()}
                        disabled={rejectChargesMutation.isPending}
                        className="inline-flex items-center gap-1 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-bold uppercase tracking-widest text-rose-800"
                      >
                        <MdThumbDown className="h-4 w-4" /> Reject
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {canChangeMode && shippingMode && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={updateModeMutation.isPending}
                className="rounded-xl bg-[#0088cc] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#0077b3] disabled:opacity-50"
              >
                Confirm shipping selection
              </button>
            </div>
          )}
        </div>
      </div>

      {showSelfModal && (
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowSelfModal(false);
          }}
        >
          <div className="w-full max-w-lg rounded-2xl border border-[#0088cc]/20 bg-[hsl(var(--card))] p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold">Self shipment — AWB</h3>
              <button type="button" onClick={() => setShowSelfModal(false)} aria-label="Close">
                <MdClose className="h-5 w-5" />
              </button>
            </div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
              Logistic partner
              <select
                className="mt-1 w-full rounded-lg border border-[#0088cc]/20 bg-[hsl(var(--background))] px-3 py-2 text-sm font-semibold normal-case tracking-normal"
                value={logisticPartnerId}
                onChange={(e) => setLogisticPartnerId(e.target.value)}
              >
                <option value="">Select partner</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
                <option value="other">Other / custom courier</option>
              </select>
            </label>
            {logisticPartnerId === 'other' && (
              <input
                className="w-full rounded-lg border border-[#0088cc]/20 bg-[hsl(var(--background))] px-3 py-2 text-sm"
                placeholder="Courier name"
                value={customPartnerName}
                onChange={(e) => setCustomPartnerName(e.target.value)}
              />
            )}
            <input
              className="w-full rounded-lg border border-[#0088cc]/20 bg-[hsl(var(--background))] px-3 py-2 text-sm"
              placeholder="AWB number"
              value={awbNumber}
              onChange={(e) => setAwbNumber(e.target.value)}
            />
            <input
              className="w-full rounded-lg border border-[#0088cc]/20 bg-[hsl(var(--background))] px-3 py-2 text-sm"
              placeholder="Tracking number (if different from AWB)"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowSelfModal(false)}
                className="rounded-xl border px-4 py-2 text-xs font-bold uppercase tracking-widest"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleProceedSelf}
                disabled={updateModeMutation.isPending}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
              >
                Save self shipment
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
